/**
 * SignUpStep - Step 1: Email + OTP with product framing; password login linked for workspace users.
 */
import React, { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, TextField, Alert, Stack, useTheme, alpha, Divider, Link,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import BoltRounded from '@mui/icons-material/BoltRounded';
import CreditCardOffRounded from '@mui/icons-material/CreditCardOffRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import TrendingDownRounded from '@mui/icons-material/TrendingDownRounded';
import { brandPalette } from '../../../theme/brandPalette';
import { config } from '../../../config';
import OnboardingLayout from '../components/OnboardingLayout';

interface SignUpStepProps {
  onSubmit: (email: string) => Promise<any>;
  loading: boolean;
  error: string | null;
  /** Start an anonymous "explore without signing up" session. */
  onExplore?: () => void;
  exploreLoading?: boolean;
}

// Tiny sparkline - muted enterprise tones (not neon)
function Sparkline({ up }: { up: boolean }) {
  const points = up
    ? '0,18 8,15 16,12 24,14 32,9 40,11 48,7 56,4 64,6 72,2'
    : '0,4 8,6 16,5 24,9 32,8 40,12 48,11 56,15 64,14 72,18';
  const color = up ? '#0f766e' : '#b45309';
  return (
    <svg width="72" height="22" viewBox="0 0 72 22" fill="none">
      <polyline points={points} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const METRICS = [
  { label: 'Monthly Revenue', value: '$1.24M', change: '+18%', up: true },
  { label: 'Conversion Rate', value: '3.8%', change: '+0.4pp', up: true },
  { label: 'Avg. CAC', value: '$142', change: '−12%', up: false },
];

const QUESTIONS = [
  'Which channel has the lowest CAC?',
  'Why did revenue spike in April?',
  'Show churn by segment last quarter.',
];

function ProductPreviewPanel() {
  const panelBg = brandPalette.neutral[200];
  const cardBg = brandPalette.neutral[100];
  const borderSubtle = brandPalette.neutral[300];
  const upColor = '#0f766e';
  const downColor = '#b45309';

  return (
    <Box sx={{
      display: { xs: 'none', lg: 'flex' },
      flexDirection: 'column',
      justifyContent: 'center',
      gap: 2,
      width: 400,
      flexShrink: 0,
      bgcolor: panelBg,
      borderLeft: '1px solid',
      borderColor: borderSubtle,
      borderRadius: '0 12px 12px 0',
      p: 3.5,
    }}>
      {/* Panel header */}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <img src={config.faviconUrl} alt={config.brandName} style={{ width: 16, height: 16, borderRadius: 3 }} />
          <Typography sx={{
            fontSize: '0.65rem', fontWeight: 600, letterSpacing: 0.9, textTransform: 'uppercase',
            color: brandPalette.neutral[600],
          }}>
            Live insights
          </Typography>
        </Stack>
        <Box sx={{ px: 0.75, py: 0.25, borderRadius: 0.75, bgcolor: brandPalette.neutral[300] }}>
          <Typography sx={{
            fontSize: '0.55rem', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
            color: brandPalette.neutral[600],
          }}>
            Sample data
          </Typography>
        </Box>
      </Stack>

      {/* KPI cards */}
      {METRICS.map((m, i) => (
        <Box key={i} sx={{
          bgcolor: cardBg,
          border: '1px solid',
          borderColor: borderSubtle,
          borderRadius: 1.5,
          px: 2, py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          boxShadow: `0 1px 2px ${alpha(brandPalette.neutral[800], 0.04)}`,
        }}>
          <Box>
            <Typography sx={{ fontSize: '0.65rem', color: brandPalette.neutral[600], mb: 0.4, fontWeight: 500 }}>
              {m.label}
            </Typography>
            <Typography sx={{
              fontSize: '1.25rem', fontWeight: 700, color: brandPalette.neutral[800], lineHeight: 1, letterSpacing: -0.4,
            }}>
              {m.value}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Sparkline up={m.up} />
            <Stack direction="row" spacing={0.4} alignItems="center" justifyContent="flex-end" sx={{ mt: 0.25 }}>
              {m.up
                ? <TrendingUpRounded sx={{ fontSize: 12, color: upColor }} />
                : <TrendingDownRounded sx={{ fontSize: 12, color: downColor }} />
              }
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: m.up ? upColor : downColor }}>
                {m.change}
              </Typography>
            </Stack>
          </Box>
        </Box>
      ))}

      <Divider sx={{ borderColor: alpha(borderSubtle, 0.9), my: 0.5 }} />

      {/* Question examples */}
      <Typography sx={{
        fontSize: '0.6rem', color: brandPalette.neutral[500], fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', mb: 0.25,
      }}>
        Ask anything
      </Typography>
      <Stack spacing={1}>
        {QUESTIONS.map((q, i) => (
          <Box key={i} sx={{
            px: 1.5, py: 0.85,
            bgcolor: cardBg,
            border: '1px solid',
            borderColor: borderSubtle,
            borderRadius: 99,
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <Box sx={{
              width: 5, height: 5, borderRadius: '50%',
              bgcolor: alpha(brandPalette.primary[500], 0.55),
              flexShrink: 0,
            }} />
            <Typography sx={{ fontSize: '0.72rem', color: brandPalette.neutral[700] }}>
              {q}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default function SignUpStep({ onSubmit, loading, error, onExplore, exploreLoading }: SignUpStepProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [email, setEmail] = useState('');
  // Forward `?redirect=` (set by auth guards on shared deep links) when the
  // user switches to the password sign-in flow, so the target survives.
  const [searchParams] = useSearchParams();
  const passwordLoginHref = (() => {
    const r = searchParams.get('redirect');
    return r ? `/login-pwd?redirect=${encodeURIComponent(r)}` : '/login-pwd';
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    await onSubmit(email.trim().toLowerCase());
  };

  const trustSignals = [
    { icon: BoltRounded, text: 'Answers in plain English' },
    { icon: ShieldOutlined, text: 'Your data stays private' },
    { icon: CreditCardOffRounded, text: 'No credit card' },
  ];

  return (
    <OnboardingLayout maxWidth={920} showLogo={false}>
      <Box sx={{
        display: 'flex',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: isDark
          ? `0 24px 64px ${alpha('#000', 0.5)}`
          : `0 1px 3px ${alpha(brandPalette.neutral[800], 0.06)}, 0 8px 24px ${alpha(brandPalette.neutral[800], 0.05)}`,
        border: '1px solid',
        borderColor: isDark ? alpha('#fff', 0.08) : brandPalette.neutral[300],
        minHeight: 520,
        bgcolor: isDark ? undefined : brandPalette.neutral[100],
      }}>

        {/* Left: form */}
        <Box sx={{
          flex: 1,
          bgcolor: isDark ? theme.palette.background.paper : brandPalette.neutral[100],
          p: { xs: 4, md: 5 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minWidth: 0,
        }}>
          {/* Logo */}
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25, mb: 4 }}>
            <img src={config.faviconUrl} alt={config.brandName} style={{ width: 32, height: 32, borderRadius: 7 }} />
            <Typography sx={{
              fontWeight: 700, fontSize: '1.05rem', letterSpacing: -0.3,
              color: isDark ? 'text.primary' : brandPalette.neutral[800],
            }}>
              {config.brandName}
            </Typography>
          </Box>

          {/* Headline — wraps on small screens, single line from sm+ */}
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.15, letterSpacing: -0.6, whiteSpace: { xs: 'normal', sm: 'nowrap' }, fontSize: { xs: '1.5rem', sm: '1.5rem' } }}>
            Decision Analyst built for Executives
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6, maxWidth: 380 }}>
            Dashboards show what happened. Klaris tells you why.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

          <form onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label="Work email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              InputLabelProps={{ required: false }}
              autoFocus
              autoComplete="email"
              placeholder="you@company.com"
              sx={{ mb: 1.5 }}
            />
            <LoadingButton
              variant="contained"
              type="submit"
              fullWidth
              loading={loading}
              disabled={exploreLoading}
              size="large"
              sx={{
                py: 1.5,
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'none',
                bgcolor: brandPalette.primary[400],
                '&:hover': { bgcolor: brandPalette.primary[500] },
                boxShadow: `0 1px 2px ${alpha(brandPalette.neutral[800], 0.08)}`,
              }}
            >
              Send verification code
            </LoadingButton>
          </form>

          {onExplore && (
            <>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 2 }}>
                <Divider sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontWeight: 600 }}>OR</Typography>
                <Divider sx={{ flex: 1 }} />
              </Stack>
              <LoadingButton
                variant="outlined"
                fullWidth
                size="large"
                loading={exploreLoading}
                disabled={loading}
                onClick={onExplore}
                startIcon={<BoltRounded />}
                sx={{
                  py: 1.4,
                  fontWeight: 700,
                  fontSize: '0.92rem',
                  textTransform: 'none',
                  color: brandPalette.primary[500],
                  borderWidth: 1.5,
                  borderColor: alpha(brandPalette.primary[400], 0.5),
                  bgcolor: isDark ? alpha(brandPalette.primary[400], 0.1) : alpha(brandPalette.primary[100], 0.55),
                  boxShadow: `0 0 0 3px ${alpha(brandPalette.primary[300], 0.12)}`,
                  '&:hover': {
                    borderWidth: 1.5,
                    borderColor: brandPalette.primary[400],
                    bgcolor: isDark ? alpha(brandPalette.primary[400], 0.18) : alpha(brandPalette.primary[100], 0.85),
                  },
                }}
              >
                Explore with sample data – no signup
              </LoadingButton>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                No email needed to start - verify later.
              </Typography>
            </>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            <Link
              component={RouterLink}
              to={passwordLoginHref}
              underline="hover"
              sx={{ fontWeight: 600, color: brandPalette.primary[400] }}
            >
              Log in with password instead
            </Link>
          </Typography>

          {/* Trust signals */}
          <Stack direction="row" spacing={3} sx={{ mt: 2.5 }}>
            {trustSignals.map((item, i) => (
              <Stack key={i} direction="row" spacing={0.6} alignItems="center">
                <item.icon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                  {item.text}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {/* reCAPTCHA disclosure (moved here from under the submit button) */}
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              This site is protected by reCAPTCHA and the Google{' '}
              <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" underline="hover">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" underline="hover">
                Terms of Service
              </Link>{' '}
              apply.
            </Typography>
          </Box>
        </Box>

        {/* Right: product preview */}
        <ProductPreviewPanel />
      </Box>
    </OnboardingLayout>
  );
}
